import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Button, 
  Icon, 
  Card, 
  Elevation, 
  NonIdealState, 
  Tag, 
  TextArea, 
  Spinner, 
  Tabs, 
  Tab, 
  MenuItem,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  InputGroup,
  Intent,
  Callout,
  EditableText,
  Tooltip
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Select } from '@blueprintjs/select';
import { observer } from 'mobx-react-lite';
import { Node } from '@xyflow/react';
import { useParams, useNavigate } from 'react-router-dom';
import Comments from '../comments/Comments';
import RepoLinks from '../links/RepoLinks';
import { MemberSelect } from '../common/MemberSelect';
import LeadSelector from '../Lead/LeadSelector';
import StatusSelector from '../Status/StatusSelector';
import RepositoryLinker from './NodeTypes/RepositoryLinker';
import TeamMemberMultiSelect from '../TeamMembers/TeamMemberMultiSelect';
import { NodeStatus } from '../../constants/nodeStatus';
import { rootStore } from '../../stores/UnifiedStoreProvider';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { debounce } from 'lodash';
import api from '../../services/api';
import './NodeDetailSidebar-rectangular.css';
import './popover-z-index-fix.css';
import './repository-search-dark.css';

// Theme-based styles
const getThemedStyles = (theme: string, isOpen: boolean) => {
  const isDark = theme === 'dark';

  return {
    sidebar: {
      height: '100%',
      backgroundColor: isDark ? '#252A31' : '#FFFFFF',
      color: isDark ? '#F5F8FA' : '#182026',
      display: 'flex',
      flexDirection: 'column' as 'column',
      transition: 'width 0.2s ease-out',
      position: 'relative' as 'relative',
      borderLeft: '1px solid var(--border)',
      overflow: 'hidden',
    },
    collapseButtonContainer: {
      position: 'absolute' as 'absolute',
      top: '10px',
      left: '10px',
      zIndex: 5,
    },
    header: {
      padding: '12px 16px',
      paddingLeft: '50px',
      borderBottom: '1px solid var(--border)',
      flexShrink: 0,
      boxSizing: 'border-box' as 'border-box',
    },
    headerText: {
      margin: 0,
      fontSize: '1.1em',
      fontWeight: 600,
      color: isOpen ? (isDark ? '#CED9E0' : '#182026') : 'transparent',
      transition: 'color 0.2s ease-out',
      whiteSpace: 'nowrap' as 'nowrap',
      overflow: 'hidden',
    },
    content: {
      padding: '16px',
      flexGrow: 1,
      overflowY: 'auto' as 'auto',
      boxSizing: 'border-box' as 'border-box',
    },
    selectedNodeInfo: { 
      display: 'flex', 
      alignItems: 'center', 
      marginBottom: '16px' 
    },
    nodeLabelText: { 
      margin: 0, 
      fontSize: '1em', 
      fontWeight: 500 
    },
    section: { 
      marginBottom: '20px' 
    },
    sectionTitle: { 
      margin: '0 0 10px 0',
      fontSize: '1.05em', 
      fontWeight: 600, 
      color: isDark ? '#BFCAD1' : '#5C7080',
    },
    sectionPlaceholder: { 
      fontSize: '0.9em', 
      color: isDark ? '#8F99A3' : '#6A7785',
    },
    hrStyle: { 
      border: 'none', 
      borderTop: '1px solid var(--border)',
      width: '100%', 
      margin: '20px 0'
    },
    repoCard: {
      marginBottom: '8px',
      backgroundColor: isDark ? '#16181C' : '#F5F8FA',
      borderColor: 'var(--border)',
      color: isDark ? '#F5F8FA' : '#182026',
      padding: '12px',
      borderRadius: '4px',
      border: '1px solid var(--border)',
    },
    linkItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      backgroundColor: isDark ? '#16181C' : '#F5F8FA',
      borderColor: 'var(--border)',
      borderRadius: '4px',
      border: '1px solid var(--border)',
      marginBottom: '6px',
      color: isDark ? '#F5F8FA' : '#182026',
    },
  };
};

interface NodeDetailSidebarProps {
  selectedNode: Node | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateNode?: (nodeId: string, data: any) => void;
  onDeleteNode?: (nodeId: string) => void;
}

const NodeDetailSidebar: React.FC<NodeDetailSidebarProps> = observer(({ 
  selectedNode, 
  isOpen, 
  onClose,
  onUpdateNode,
  onDeleteNode 
}) => {
  const { mindmapId, orgSlug } = useParams<{ mindmapId?: string; orgSlug?: string }>();
  const navigate = useNavigate();
  const appStore = useAppStore();
  const { mindmap, theme: themeStore } = appStore;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(480);
  // Only use local state for UI-specific things, not node data
  const [localNotes, setLocalNotes] = useState(''); // For debouncing only
  const [localLabel, setLocalLabel] = useState(''); // For inline editing
  const [users, setUsers] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]); // Added missing state
  const [isLoading, setIsLoading] = useState(false);
  
  // Link dialog state
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  
  // Get effective theme from theme store
  const theme = themeStore.isDark ? 'dark' : 'light';
  
  const styles = getThemedStyles(theme, !isCollapsed);
  
  // Get the actual node from the store using selectedNodeId
  const nodeId = mindmap.selectedNodeId;
  const storeNode = nodeId ? mindmap.nodes.find(n => n.id === nodeId) : null;
  
  // Load documents when node is selected
  useEffect(() => {
    if (nodeId && isOpen) {
      console.log(`📥 [NodeDetailSidebar] Loading documents for node: ${nodeId}`);
      mindmap.loadNodeDocs(nodeId).catch(err => {
        console.error('[NodeDetailSidebar] Error loading documents:', err);
      });
    }
  }, [nodeId, isOpen]);
  
  // Get values directly from the store node - these will auto-update when store changes!
  const notes = storeNode?.notes || '';
  const lead = storeNode?.lead || '';
  const status = storeNode?.status || null;
  const links = storeNode?.links || [];
  const supportMembers = storeNode?.supportMembers || [];
  const followers = storeNode?.followers || [];
  
  // Get linked repositories directly from store - this is reactive and will update immediately
  // whenever mindmap.getNodeGithubLinks changes (i.e., when a repo is linked/unlinked)
  const linkedRepositories = nodeId 
    ? mindmap.getNodeGithubLinks(nodeId).map((link: any) => ({
        id: link.metadata?.id || 0, // GitHub repository ID
        linkId: link.id, // Database link ID for deletion
        name: link.repositoryName,
        full_name: link.repositoryFullName,
        html_url: link.repositoryUrl,
        description: link.metadata?.description
      }))
    : [];
  
  // Sync local state for editing when node changes - ONLY for UI purposes
  useEffect(() => {
    if (storeNode) {
      console.log('📊 Node data updated in store:', storeNode);
      setLocalNotes(storeNode.notes || '');
      setLocalLabel(storeNode.label || 'New Node');
      
      // Fetch activities for the node (mock data for now)
      setActivities([
        {
          id: '1',
          user: { name: 'John Doe', avatar: '' },
          action: 'changed priority to',
          object: 'High',
          value: '',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)
        },
        {
          id: '2', 
          user: { name: 'Jane Smith', avatar: '' },
          action: 'added link to',
          object: 'Project Phoenix',
          value: '',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000)
        }
      ]);
    }
  }, [storeNode?.id]); // Only watch node ID changes, not every field
  
  // Fetch users for lead dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Auth is handled via httpOnly cookies
        
        const response = await api.get(
          '/users',
          
        );
        
        setUsers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        // Use mock data if fetch fails
        setUsers([
          { id: 'user-1', name: 'Dev User 1', email: 'dev1@example.com' },
          { id: 'user-2', name: 'Dev User 2', email: 'dev2@example.com' },
        ]);
      }
    };
    
    fetchUsers();
  }, []);
  
  const handleToggleSidebar = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    if (!newCollapsed && sidebarWidth <= 60) {
      setSidebarWidth(480);
    }
  };
  
  // Debounced notes update
  const debouncedNotesUpdate = useCallback(
    debounce((nodeId: string, value: string) => {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { notes: value });
    }, 500),
    []
  );
  
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNotes = e.target.value;
    setLocalNotes(newNotes);
    
    // Save notes after debounce
    if (nodeId) {
      debouncedNotesUpdate(nodeId, newNotes);
    }
  };
  
  const handleLeadChange = (value: string | null) => {
    // Directly update the store, no local state
    if (nodeId) {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { lead: value || '' });
    }
  };
  
  const handleSupportMembersChange = (userIds: string[]) => {
    // Update support members in store
    if (nodeId) {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { supportMembers: userIds });
    }
  };
  
  const handleFollowersChange = (userIds: string[]) => {
    // Update followers in store
    if (nodeId) {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { followers: userIds });
    }
  };
  
  const handleStatusChange = (value: NodeStatus | null) => {
    // Directly update the store, no local state
    if (nodeId) {
      rootStore.mindmap.updateNodeOptimistically(nodeId, { status: value });
    }
  };
  
  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleAddLink = () => {
    // Open the dialog instead of using prompt
    setIsLinkDialogOpen(true);
    setLinkError(null);
    setLinkName('');
    setLinkUrl('');
  };

  const handleConfirmAddLink = async () => {
    if (!linkName.trim()) {
      setLinkError('Link name is required');
      return;
    }

    if (!linkUrl.trim()) {
      setLinkError('Link URL is required');
      return;
    }

    if (!validateUrl(linkUrl)) {
      setLinkError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setLinkLoading(true);
    setLinkError(null);

    try {
      if (nodeId) {
        // Store as an object with name and URL
        const newLink = {
          id: crypto.randomUUID(),
          name: linkName.trim(),
          url: linkUrl.trim(),
          createdAt: Date.now()
        };
        const newLinks = [...links, newLink];
        rootStore.mindmap.updateNodeOptimistically(nodeId, { links: newLinks });
        
        // Reset and close dialog
        setLinkName('');
        setLinkUrl('');
        setIsLinkDialogOpen(false);
      }
    } catch (err: any) {
      console.error('[NodeDetailSidebar] Error adding link:', err);
      setLinkError(`Failed to add link: ${err.message}`);
    } finally {
      setLinkLoading(false);
    }
  };
  
  const handleRemoveLink = (index: number) => {
    if (nodeId) {
      const newLinks = links.filter((_, i) => i !== index);
      rootStore.mindmap.updateNodeOptimistically(nodeId, { links: newLinks });
    }
  };

  const handleRepositoriesChange = (repos: any[]) => {
    // No-op: repositories are now managed by the store
    // The observer pattern will automatically update the UI when store changes
    console.log('[NodeDetailSidebar] Repositories changed (managed by store):', repos);
  };
  
  // Only hide if explicitly not open
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`${theme === 'dark' ? 'bp5-dark' : ''} node-detail-sidebar ${isCollapsed ? 'collapsed' : ''}`}
      style={{
        height: '100%',
        width: '100%',
        maxWidth: sidebarWidth,
        backgroundColor: theme === 'dark' ? '#252a31' : '#ffffff',
        borderLeft: theme === 'dark' ? '1px solid #383e47' : '1px solid #e1e4e8',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative', /* Ensure it stays in flow */
      }}
      data-testid="node-detail-sidebar"
      data-theme={theme}
    >
      <div style={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: theme === 'dark' ? '1px solid #383e47' : '1px solid #e1e4e8',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        backgroundColor: theme === 'dark' ? '#2f343c' : 'transparent',
      }}>
        <EditableText
          value={localLabel}
          onChange={(value) => {
            // Update local state immediately for responsive typing
            setLocalLabel(value);
          }}
          onConfirm={(value) => {
            const finalLabel = value.trim() || 'New Node';
            setLocalLabel(finalLabel);
            
            console.log('✏️ Confirming label update to:', finalLabel, 'for node:', nodeId);
            if (nodeId) {
              rootStore.mindmap.updateNodeOptimistically(nodeId, { label: finalLabel });
            }
          }}
          placeholder="Node label"
          selectAllOnFocus={true}
          className="node-detail-title"
          style={{
            fontSize: '16px',
            fontWeight: 600,
            flex: 1,
            marginRight: '8px',
          }}
        />
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            icon={IconNames.CHEVRON_RIGHT}
            minimal
            small
            onClick={onClose}
            title="Collapse sidebar"
          />
          <Button
            icon={IconNames.CROSS}
            minimal
            small
            onClick={onClose}
            title="Close sidebar"
          />
        </div>
      </div>
      
      
      <div style={{ ...styles.content, flex: 1, overflow: 'auto' }} className="node-detail-sidebar-content">
          {storeNode ? (
            <>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                  <Spinner size={24} />
                  <p style={{ margin: '15px 0 0 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Loading node details...
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ opacity: 1, transition: 'opacity 0.3s ease-in-out' }}>
                    {/* Node Info */}
                    <div style={styles.selectedNodeInfo}>
                      <Icon
                        icon={IconNames.BOX}
                        style={{ marginRight: '10px', color: '#E91E63' }}
                        size={18}
                      />
                      <p style={styles.nodeLabelText}>
                        {storeNode.label || 'No Label'}
                      </p>
                    </div>
                    <hr className="sidebar-divider" />

                    {/* Status and Lead Row */}
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }} data-section="status">
                      {/* Status Section */}
                      <div style={{ flex: 1 }}>
                        <Tooltip
                          content="Track the current status of this node (e.g., Not Started, In Progress, Complete)"
                          position="bottom"
                          hoverOpenDelay={300}
                        >
                          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', cursor: 'help' }}>
                            Status
                          </div>
                        </Tooltip>
                        <StatusSelector
                          currentStatus={status}
                          onStatusSelect={handleStatusChange}
                          minimal={false}
                          showLabel={false}
                        />
                      </div>
                      
                      {/* Lead Assignment Section */}
                      <div style={{ flex: 1 }}>
                        <Tooltip
                          content="Assign a lead person responsible for this node"
                          position="bottom"
                          hoverOpenDelay={300}
                        >
                          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', cursor: 'help' }}>
                            Lead
                          </div>
                        </Tooltip>
                        <LeadSelector
                          currentLeadId={lead}
                          onLeadSelect={handleLeadChange}
                          minimal={false}
                          showLabel={false}
                        />
                      </div>
                    </div>

                    {/* Support Members Section */}
                    <div style={{ marginBottom: '16px' }} data-section="team-members">
                      <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        Support Members
                      </div>
                      <TeamMemberMultiSelect
                        selectedUserIds={supportMembers}
                        onUserIdsChange={handleSupportMembersChange}
                        placeholder="Add support members..."
                        tagMinimal={true}
                      />
                    </div>

                    {/* Followers Section */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        Followers
                      </div>
                      <TeamMemberMultiSelect
                        selectedUserIds={followers}
                        onUserIdsChange={handleFollowersChange}
                        placeholder="Add followers..."
                        tagMinimal={true}
                      />
                    </div>
                    <hr className="sidebar-divider" />

                    {/* Documents Section */}
                    <div style={styles.section}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Tooltip
                          content="Create and manage multiple documents for this node"
                          position="bottom"
                          hoverOpenDelay={300}
                        >
                          <h3 style={{ ...styles.sectionTitle, marginBottom: 0, cursor: 'help' }}>
                            Documents ({mindmap.getNodeDocs(nodeId || '').length})
                          </h3>
                        </Tooltip>
                        <Button
                          icon={IconNames.PLUS}
                          intent="primary"
                          minimal
                          small
                          onClick={() => {
                            if (orgSlug && mindmapId && nodeId) {
                              // Create a new document and navigate to it
                              mindmap.createNodeDoc(nodeId).then((doc: any) => {
                                if (doc) {
                                  navigate(`/${orgSlug}/mindmap/${mindmapId}/node/${nodeId}/doc?docId=${doc.id}`);
                                }
                              });
                            }
                          }}
                          title="Create new document"
                        >
                          Create Document
                        </Button>
                      </div>
                      
                      {/* List of documents */}
                      {mindmap.getNodeDocs(nodeId || '').length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                          {mindmap.getNodeDocs(nodeId || '').map((doc: any) => (
                            <div
                              key={doc.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                backgroundColor: 'var(--surface-2)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                              }}
                              onClick={() => {
                                if (orgSlug && mindmapId && nodeId) {
                                  navigate(`/${orgSlug}/mindmap/${mindmapId}/node/${nodeId}/doc?docId=${doc.id}`);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--surface-3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--surface-2)';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                <Icon icon={IconNames.DOCUMENT} size={14} color="var(--text-primary)" />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ 
                                    fontSize: '13px', 
                                    fontWeight: 500, 
                                    color: 'var(--text-primary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {doc.title || 'Untitled Document'}
                                  </div>
                                  {doc.plainTextContent && (
                                    <div style={{ 
                                      fontSize: '11px', 
                                      color: 'var(--text-muted)',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {doc.plainTextContent.substring(0, 50)}
                                      {doc.plainTextContent.length > 50 ? '...' : ''}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <Button
                                icon={IconNames.TRASH}
                                minimal
                                small
                                intent="danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Are you sure you want to delete this document?')) {
                                    mindmap.deleteNodeDoc(nodeId || '', doc.id);
                                  }
                                }}
                                title="Delete document"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '16px', 
                          color: 'var(--text-muted)',
                          fontSize: '12px',
                        }}>
                          No documents yet. Create your first document!
                        </div>
                      )}
                    </div>
                    <hr className="sidebar-divider" />

                    {/* General Notes Section */}
                    <div style={styles.section}>
                      <h3 style={styles.sectionTitle}>General Notes</h3>
                        <TextArea
                          fill
                          growVertically
                          placeholder="Add detailed notes here…"
                          rows={5}
                          value={localNotes}
                        onChange={handleNotesChange}
                          style={{ minHeight: '100px' }}
                        />
                    </div>
                    <hr className="sidebar-divider" />

                    {/* Comments Section - Modern UI without separate header */}
                    <div style={styles.section} data-section="comments">
                      <Comments nodeId={nodeId || ''} />
                    </div>
                    <hr className="sidebar-divider" />

                                  {/* Links Section */}
              <div style={styles.section} data-section="links">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={styles.sectionTitle}>Links ({links.length})</h3>
                        <Button text="Add Link" icon={IconNames.PLUS} minimal small onClick={handleAddLink} />
                      </div>
                      {links.length === 0 ? (
                        <p style={styles.sectionPlaceholder}>No links added</p>
                      ) : (
                        links.map((link, index) => (
                          <div key={index} style={styles.linkItem}>
                            <Icon icon={IconNames.LINK} style={{ marginRight: '8px' }} />
                            <a 
                              href={typeof link === 'string' ? link : link.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{ flex: 1 }}
                              title={typeof link === 'string' ? link : `${link.name}\n${link.url}`}
                            >
                              {typeof link === 'string' ? link : link.name}
                            </a>
                            <Button
                              icon={IconNames.TRASH}
                              minimal
                              small
                              onClick={() => handleRemoveLink(index)}
                            />
                          </div>
                        ))
                      )}
                    </div>
                    <hr className="sidebar-divider" />

                    {/* Repositories Section */}
                    <div style={styles.section} data-section="repositories">
                      <RepoLinks
                        linkedRepositories={linkedRepositories}
                        onRepositoriesChange={handleRepositoriesChange}
                        nodeId={nodeId || ''}
                        mindmapId={mindmapId}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <NonIdealState 
                icon={IconNames.SEARCH_TEMPLATE} 
                title="No Node Selected" 
                description={<p style={styles.sectionPlaceholder}>Click on a node to see its details.</p>} 
            />
          )}
      </div>

      {/* Add Link Dialog */}
      <Dialog
        isOpen={isLinkDialogOpen}
        onClose={() => setIsLinkDialogOpen(false)}
        title="Add Link"
        style={{ width: '400px' }}
        className={theme === 'dark' ? 'bp5-dark' : ''}
      >
        <DialogBody>
          {linkError && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: '15px' }}>
              {linkError}
            </Callout>
          )}
          
          <FormGroup
            label="Link Name"
            labelFor="link-name"
            helperText="A descriptive name for the link"
          >
            <InputGroup
              id="link-name"
              placeholder="e.g., Project Documentation"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
              disabled={linkLoading}
              autoFocus
            />
          </FormGroup>
          
          <FormGroup
            label="URL"
            labelFor="link-url"
            helperText="The full URL including https://"
          >
            <InputGroup
              id="link-url"
              placeholder="e.g., https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              disabled={linkLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && linkName.trim() && linkUrl.trim()) {
                  handleConfirmAddLink();
                }
              }}
            />
          </FormGroup>
        </DialogBody>
        
        <DialogFooter
          actions={
            <>
              <Button 
                onClick={() => setIsLinkDialogOpen(false)}
                disabled={linkLoading}
              >
                Cancel
              </Button>
              <Button
                intent={Intent.PRIMARY}
                onClick={handleConfirmAddLink}
                loading={linkLoading}
                disabled={!linkName.trim() || !linkUrl.trim()}
              >
                Add Link
              </Button>
            </>
          }
        />
      </Dialog>
    </div>
  );
});

export default NodeDetailSidebar; 